import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './AdditionalCostsEditor.css';
import numeral from 'numeral';
import Button from 'components/shared/button/Button';
import Dropdown from 'components/shared/dropdown/Dropdown';
import Input from 'components/shared/input/Input';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';

export default class AdditionalCostsEditor extends Component {
  static propTypes = {
    adjustmentDesc: PropTypes.string,
    adjustment: PropTypes.string,
    miscItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        cost: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    sign: PropTypes.oneOf([1, -1]),
    onAdjustmentDescChange: PropTypes.func,
    onAdjustmentChange: PropTypes.func,
    onAdjustmentSignChange: PropTypes.func,
    onMiscAdd: PropTypes.func,
    onMiscDelete: PropTypes.func,
  };

  static defaultProps = {
    adjustmentDesc: '',
    adjustment: '',
    miscItems: [],
    sign: 1,
  };

  constructor(props) {
    super(props);
    this.state = {
      newCost: '',
      newDescription: '',
      showDeleteConfirmation: false,
      showNewLine: false,
    };
    this.deleteInfo = null; // { id: number, index: number }
  }

  render() {
    let adjustmentValue = null;
    if (numeral(this.props.adjustment).value() !== null) {
      adjustmentValue = Math.abs(numeral(this.props.adjustment).value());
    }

    let adjustmentInputClassName = null;
    if (adjustmentValue !== 0) {
      adjustmentInputClassName =
        this.props.sign === 1 ? 'positive' : 'negative';
    }

    return (
      <div className="additional-costs-editor">
        <table>
          <colgroup>
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th className="text-uppercase">description</th>
              <th className="text-uppercase">cost</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {this.props.miscItems.map((item, index) => {
              return (
                <tr key={index}>
                  <td className="h4">{item.description}</td>
                  <td className="body-regular">
                    {numeral(item.cost).format('$0,0.00')}
                  </td>
                  <td>
                    <Button
                      className="button-blue-grey"
                      label="Delete"
                      onClick={() => this.handleDelete(item.id, index)}
                    />
                  </td>
                </tr>
              );
            })}
            {this.state.showNewLine && (
              <tr>
                <td>
                  <Input
                    className="new-line-description"
                    value={this.state.newDescription}
                    onChange={this.handleNewDescriptionChange}
                  />
                </td>
                <td>
                  <Input
                    type="number"
                    value={this.state.newCost}
                    onChange={this.handleNewCostChange}
                  />
                </td>
                <td className="new-line-button-container">
                  <Button
                    className="button-gray"
                    disabled={
                      this.state.newCost === '' ||
                      this.state.newDescription === ''
                    }
                    label="Confirm"
                    onClick={this.handleNewConfirm}
                  />
                  <Button
                    className="button-white"
                    label="Cancel"
                    onClick={this.handleNewCancel}
                  />
                </td>
              </tr>
            )}
            <tr>
              <td>
                <Button
                  className="button-orange"
                  label="Add"
                  onClick={this.handleAdd}
                />
              </td>
              <td />
              <td />
            </tr>
            <tr>
              <td>
                <div className="adjustment-container">
                  <div className="text-uppercase">adjustment</div>
                  <Input
                    className="new-line-description"
                    value={this.props.adjustmentDesc}
                    onChange={this.handleAdjustmentDescChange}
                  />
                  <div>
                    <Dropdown
                      className="positive-negative-drop-down"
                      options={[
                        {
                          value: 1,
                          label: '+',
                        },
                        {
                          value: -1,
                          label: '-',
                        },
                      ]}
                      placeholder=""
                      value={this.props.sign}
                      onChange={this.handleAdjustmentSignChange}
                    />
                  </div>
                </div>
              </td>
              <td>
                <Input
                  className={`adjustment-input ${adjustmentInputClassName}`}
                  type="number"
                  value={
                    adjustmentValue !== null ? adjustmentValue.toString() : ''
                  }
                  onChange={this.handleAdjustmentChange}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <ConfirmationModal
          show={this.state.showDeleteConfirmation}
          message={'Are you sure you want to delete this item?'}
          onHide={this.handleConfirmationModalHide}
        />
      </div>
    );
  }

  handleAdd = () => {
    this.setState({
      showNewLine: true,
    });
  };

  handleAdjustmentDescChange = (value) => {
    if (this.props.onAdjustmentDescChange) {
      this.props.onAdjustmentDescChange(value);
    }
  };

  handleAdjustmentChange = (value) => {
    if (this.props.onAdjustmentChange) {
      let newValue = numeral(value).value();
      if (newValue === null) {
        this.props.onAdjustmentChange('');
      } else {
        if (this.props.sign === -1) {
          newValue = newValue * -1;
        }
        this.props.onAdjustmentChange(newValue.toString());
      }
    }
  };

  handleAdjustmentSignChange = (option) => {
    if (this.props.onAdjustmentSignChange) {
      this.props.onAdjustmentSignChange(option.value);
    }
  };

  handleConfirmationModalHide = (confirmation) => {
    if (confirmation && this.props.onMiscDelete) {
      this.props.onMiscDelete(this.deleteInfo.id, this.deleteInfo.index);
    }
    this.setState({
      showDeleteConfirmation: false,
    });
  };

  handleDelete = (itemId, index) => {
    this.deleteInfo = {
      id: itemId,
      index,
    };
    this.setState({
      showDeleteConfirmation: true,
    });
  };

  handleNewCancel = () => {
    this.setState({
      newCost: '',
      newDescription: '',
      showNewLine: false,
    });
  };

  handleNewConfirm = () => {
    if (this.props.onMiscAdd) {
      this.props.onMiscAdd(this.state.newDescription, this.state.newCost);
    }
    this.setState({
      newCost: '',
      newDescription: '',
      showNewLine: false,
    });
  };

  handleNewCostChange = (value) => {
    this.setState({
      newCost: value,
    });
  };

  handleNewDescriptionChange = (value) => {
    this.setState({
      newDescription: value,
    });
  };
}



// WEBPACK FOOTER //
// ./src/components/admin/invoices/AdditionalCostsEditor.js